import { useEffect, useState, useRef } from "react";

/**
 * Hook customizado para animação de placeholder com sugestões rotativas
 */

const placeholderSuggestions = [
  "Aluno do sexo masculino, 30 anos, 75.5 kg, 175 cm de altura...",
  "Braço direito 35 cm, braço esquerdo 34 cm. Tórax 95 cm, cintura 82 cm...",
  "Dobra tricipal 12 mm, subescapular 15 mm. Bioimpedância: 18% gordura...",
  "Altura 170 cm, peso 68 kg. Circunferências: quadril 92 cm, coxa 55 cm...",
];

interface UseAnimatedPlaceholderProps {
  isOpen: boolean;
  isAIModalOpen: boolean;
  aiInputValue: string;
}

export function useAnimatedPlaceholder({
  isOpen,
  isAIModalOpen,
  aiInputValue,
}: UseAnimatedPlaceholderProps) {
  const [animatedPlaceholder, setAnimatedPlaceholder] = useState("");
  const [suggestionIndex, setSuggestionIndex] = useState(0);
  const animationRef = useRef<NodeJS.Timeout | null>(null);
  const charIndexRef = useRef(0);
  const isDeletingRef = useRef(false);

  useEffect(() => {
    if (!isOpen || isAIModalOpen || aiInputValue) {
      if (animationRef.current) {
        clearTimeout(animationRef.current);
        animationRef.current = null;
      }
      setAnimatedPlaceholder("");
      return;
    }

    const currentSuggestion = placeholderSuggestions[suggestionIndex];
    charIndexRef.current = 0;
    isDeletingRef.current = false;

    const animate = () => {
      if (
        !isDeletingRef.current &&
        charIndexRef.current <= currentSuggestion.length
      ) {
        setAnimatedPlaceholder(
          currentSuggestion.slice(0, charIndexRef.current)
        );
        charIndexRef.current++;
        animationRef.current = setTimeout(animate, 50);
      } else if (
        !isDeletingRef.current &&
        charIndexRef.current > currentSuggestion.length
      ) {
        // Espera antes de começar a deletar
        animationRef.current = setTimeout(() => {
          isDeletingRef.current = true;
          charIndexRef.current = currentSuggestion.length;
          animate();
        }, 2000);
      } else if (isDeletingRef.current && charIndexRef.current > 0) {
        charIndexRef.current--;
        setAnimatedPlaceholder(
          currentSuggestion.slice(0, charIndexRef.current)
        );
        animationRef.current = setTimeout(animate, 30);
      } else {
        // Passa para próxima sugestão
        setSuggestionIndex(
          (prev) => (prev + 1) % placeholderSuggestions.length
        );
        setAnimatedPlaceholder("");
      }
    };

    animationRef.current = setTimeout(animate, 500);

    return () => {
      if (animationRef.current) {
        clearTimeout(animationRef.current);
        animationRef.current = null;
      }
    };
  }, [isOpen, isAIModalOpen, aiInputValue, suggestionIndex]);

  return animatedPlaceholder;
}
